import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="company-autocomplete"
// Works with the generic autocomplete controller to handle company selection
// and populate address fields with Companies House registered office address
export default class extends Controller {
  static targets = [
    "companyName",
    "companyNumber",
    // Address fields - matching address_autocomplete_controller targets
    "line1", "line2", "line3", "line4",
    "townOrCity", "county", "postcode", "country",
    "district", "locality", "thoroughfare",
    "buildingName", "subBuildingName", "subBuildingNumber", "buildingNumber",
    "latitude", "longitude", "residential", "formattedAddress"
  ]

  connect() {
    console.log("Company autocomplete controller connected")
  }

  // Handle selection event from autocomplete controller
  async handleSelect(event) {
    const { id, title } = event.detail
    const element = event.detail.element

    if (!id) {
      console.error("Company autocomplete: No company number provided in selection")
      return
    }

    try {
      // Show loading state on the input if available
      if (this.hasCompanyNameTarget) {
        this.companyNameTarget.classList.add("loading")
      }

      const company = await this.fetchCompany(id)
      if (company) {
        this.populateFields(company)
      }
    } catch (error) {
      console.error("Company autocomplete error:", error)
      // If we can't fetch company details, at least set the name from the selection
      if (this.hasCompanyNameTarget && title) {
        // Remove any status badge from the title and convert to title case
        const cleanName = title.replace(/\s*\([^)]*\)\s*$/, "").trim()
        this.companyNameTarget.value = this.toTitleCase(cleanName)
      }
    } finally {
      if (this.hasCompanyNameTarget) {
        this.companyNameTarget.classList.remove("loading")
      }
    }
  }

  // Fetch full company details from backend
  async fetchCompany(companyNumber) {
    const response = await fetch(`/companies_house/search/${encodeURIComponent(companyNumber)}`, {
      headers: {
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return await response.json()
  }

  // Populate all form fields with company data
  populateFields(company) {
    // Set company name (official name from Companies House, converted to title case)
    if (this.hasCompanyNameTarget && company.company_name) {
      this.companyNameTarget.value = this.toTitleCase(company.company_name)
    }

    // Set company number
    if (this.hasCompanyNumberTarget && company.company_number) {
      this.companyNumberTarget.value = company.company_number
    }

    // Populate address fields from registered office address
    const address = company.registered_office_address
    if (address) {
      this.populateAddressFields(address)
    }

    // Trigger change events for validation
    this.triggerChangeEvents()
  }

  // Populate address fields from Companies House address data
  populateAddressFields(address) {
    // Build line 1 from premises and address_line_1
    let line1 = ""
    if (address.premises) {
      line1 = address.premises
      if (address.line_1) {
        line1 += ", " + address.line_1
      }
    } else {
      line1 = address.line_1 || ""
    }
    this.setFieldValue("line1", line1)

    // Line 2 from address_line_2
    this.setFieldValue("line2", address.line_2 || "")

    // Town/City from locality
    this.setFieldValue("townOrCity", address.locality || "")

    // County from region
    this.setFieldValue("county", address.region || "")

    // Postcode
    this.setFieldValue("postcode", address.postcode || "")

    // Country
    this.setFieldValue("country", address.country || "United Kingdom")

    // Clear other fields that might have been populated previously
    this.setFieldValue("line3", "")
    this.setFieldValue("line4", "")
    this.setFieldValue("district", "")
    this.setFieldValue("locality", address.locality || "")
    this.setFieldValue("thoroughfare", "")
    this.setFieldValue("buildingName", address.premises || "")
    this.setFieldValue("buildingNumber", "")
    this.setFieldValue("subBuildingName", "")
    this.setFieldValue("subBuildingNumber", "")
    this.setFieldValue("latitude", "")
    this.setFieldValue("longitude", "")

    // Set residential to false for companies
    if (this.hasResidentialTarget) {
      this.residentialTarget.checked = false
    }

    // Store formatted address as JSON array
    if (this.hasFormattedAddressTarget) {
      const formattedArray = [
        line1,
        address.line_2,
        address.locality,
        address.region,
        address.postcode
      ].filter(Boolean)
      this.formattedAddressTarget.value = JSON.stringify(formattedArray)
    }
  }

  // Set field value if target exists
  setFieldValue(targetName, value) {
    const capitalizedName = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    // Check has<Name>Target BEFORE reading <name>Target: accessing the singular
    // target getter when the element is absent THROWS Stimulus's "Missing target
    // element" error (caught upstream, but it aborts the rest of populateAddressFields
    // and spams the console). Forms that only render a subset of address targets
    // (admin Create Team, free-trial) hit this for line3/line4/etc.
    const hasTarget = this[`has${capitalizedName}Target`]
    if (!hasTarget) return

    this[`${targetName}Target`].value = value
  }

  // Trigger change events on all populated fields
  triggerChangeEvents() {
    const allTargets = [
      "companyName", "companyNumber",
      "line1", "line2", "line3", "line4",
      "townOrCity", "county", "postcode", "country",
      "district", "locality", "thoroughfare",
      "buildingName", "subBuildingName", "subBuildingNumber", "buildingNumber",
      "latitude", "longitude"
    ]

    allTargets.forEach(targetName => {
      const capitalizedName = targetName.charAt(0).toUpperCase() + targetName.slice(1)
      // Same guard-before-access fix as setFieldValue: never read <name>Target
      // unless has<Name>Target is true, or Stimulus throws for absent targets.
      if (!this[`has${capitalizedName}Target`]) return

      this[`${targetName}Target`].dispatchEvent(new Event("change", { bubbles: true }))
    })

    // Trigger change on residential checkbox
    if (this.hasResidentialTarget) {
      const event = new Event("change", { bubbles: true })
      this.residentialTarget.dispatchEvent(event)
    }
  }

  // Convert text to title case (first letter of each word uppercase)
  // Handles common business abbreviations like LTD, PLC, LLP, etc.
  toTitleCase(text) {
    if (!text) return text
    
    // Words that should stay uppercase
    const upperCaseWords = ['LTD', 'PLC', 'LLP', 'LLC', 'UK', 'USA', 'EU', 'CIC']
    
    // Words that should stay lowercase (unless first word)
    const lowerCaseWords = ['and', 'of', 'the', 'in', 'for', 'to', 'a', 'an']
    
    return text
      .toLowerCase()
      .split(' ')
      .map((word, index) => {
        const upperWord = word.toUpperCase()
        
        // Check if it should stay uppercase
        if (upperCaseWords.includes(upperWord)) {
          return upperWord
        }
        
        // Check if it should stay lowercase (but not if first word)
        if (index > 0 && lowerCaseWords.includes(word)) {
          return word
        }
        
        // Title case: capitalize first letter
        return word.charAt(0).toUpperCase() + word.slice(1)
      })
      .join(' ')
  }
}
